import React from 'react';
import { Autocomplete, AutocompleteItem, Chip } from '@heroui/react';
import type { Key } from '@react-types/shared';

interface CompetitorItem {
  key: string;
  label: string;
}

// Hardcoded competitors list (API endpoint doesn't exist)
const COMPETITORS_LIST: CompetitorItem[] = [
  { key: 'hotjar', label: 'Hotjar' },
  { key: 'fullstory', label: 'FullStory' },
  { key: 'mixpanel', label: 'Mixpanel' },
  { key: 'mxpnl', label: 'Mxpnl' },
  { key: 'contentsquare', label: 'Contentsquare' },
  { key: 'crazyegg', label: 'Crazy Egg' },
  { key: 'mouseflow', label: 'Mouseflow' },
  { key: 'smartlook.com', label: 'Smartlook' },
  { key: 'clarity.ms', label: 'Clarity' },
  { key: 'logrocket', label: 'LogRocket' },
  { key: 'heapanalytics', label: 'Heap Analytics' },
  { key: 'pendo.io', label: 'Pendo' },
  { key: 'luckyorange', label: 'Lucky Orange' },
  { key: 'amplitude.com', label: 'Amplitude' },
  { key: 'usertesting', label: 'UserTesting' },
  { key: 'posthog', label: 'PostHog' }
];

interface FilterCompetitorsMultiSelectProps {
  label: string;
  value: string; // comma-separated competitor keys
  onChange: (value: string) => void;
  className?: string;
}

export function FilterCompetitorsMultiSelect({
  label,
  value,
  onChange,
  className = 'w-64'
}: FilterCompetitorsMultiSelectProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCompetitors, setSelectedCompetitors] = React.useState<CompetitorItem[]>([]);
  const allCompetitors = COMPETITORS_LIST;

  // Parse comma-separated keys into array
  const selectedCompetitorKeys = React.useMemo(() => {
    if (!value || value.trim() === '') {
      return [];
    }
    return value
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k !== '');
  }, [value]);

  // Update selected competitors when value changes
  React.useEffect(() => {
    if (selectedCompetitorKeys.length === 0) {
      setSelectedCompetitors([]);
      return;
    }

    const selected = allCompetitors.filter((comp) => selectedCompetitorKeys.includes(comp.key));
    setSelectedCompetitors(selected);
  }, [selectedCompetitorKeys.join(','), allCompetitors]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return allCompetitors.filter((comp) => !selectedCompetitorKeys.includes(comp.key));
    }
    const term = searchTerm.toLowerCase();
    return allCompetitors.filter(
      (comp) =>
        !selectedCompetitorKeys.includes(comp.key) &&
        (comp.label.toLowerCase().includes(term) || comp.key.toLowerCase().includes(term))
    );
  }, [searchTerm, selectedCompetitorKeys, allCompetitors]);

  const handleCompetitorAdd = React.useCallback(
    (key: Key | null) => {
      if (!key) return;

      const competitorKey = String(key);
      const competitor =
        selectedCompetitors.find((c) => c.key === competitorKey) || allCompetitors.find((c) => c.key === competitorKey);

      if (!competitor || selectedCompetitorKeys.includes(competitorKey)) return;

      const newSelectedCompetitors = [...selectedCompetitors, competitor];
      setSelectedCompetitors(newSelectedCompetitors);

      // Update value with comma-separated keys
      const newValue = newSelectedCompetitors.map((c) => c.key).join(',');
      onChange(newValue);

      setSearchTerm('');
    },
    [selectedCompetitors, allCompetitors, selectedCompetitorKeys, onChange]
  );

  const handleCompetitorRemove = React.useCallback(
    (competitorKey: string) => {
      const newSelectedCompetitors = selectedCompetitors.filter((c) => c.key !== competitorKey);
      setSelectedCompetitors(newSelectedCompetitors);

      const newValue = newSelectedCompetitors.map((c) => c.key).join(',');
      onChange(newValue);
    },
    [selectedCompetitors, onChange]
  );

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="text-default-700 text-sm font-medium">{label}</label>
      <Autocomplete
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        selectedKey={null}
        onSelectionChange={handleCompetitorAdd}
        isLoading={false}
        placeholder="Search competitors..."
        size="sm"
        allowsCustomValue={false}
        onOpenChange={() => {}}
      >
        {filteredOptions.map((competitor) => (
          <AutocompleteItem key={competitor.key} textValue={competitor.label}>
            <div className="flex flex-col">
              <span className="font-medium">{competitor.label}</span>
            </div>
          </AutocompleteItem>
        ))}
      </Autocomplete>
      {selectedCompetitors.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedCompetitors.map((competitor) => (
            <Chip key={competitor.key} size="sm" variant="flat" onClose={() => handleCompetitorRemove(competitor.key)}>
              {competitor.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterCompetitorsMultiSelect;
